import React, { useEffect, useState, useMemo } from "react";
import { getMonthName } from "../../services/appService";
import geo from "../../services/geoService";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";

const ViewCandidate = ({
  viewShowHide,
  handleViewShowHide,
  viewType,
  selectedObj,
  handleDurationShowHide,
  filterSubcontractorList,
  filterRecruiterList,
  title,
  onClose,
  wfTask,
  embedded,
  hideHeader,
}) => {
  const [stateList, setStateList] = useState([]);
  const [subContractorList, setSubContractorList] = useState([]);
  const [recruiterList, setRecruiterList] = useState([]);
  const [aWorkfinityAppEntity, setAWorkfinityAppEntity] = useState({});
  const [entityName, setEntityName] = useState("");

  const candidateEntityOptions = [
    { id: 1, name: "C2C Basis" },
    { id: 2, name: "W2 Basis" },
    { id: 3, name: "1099 Basis" },
  ];

  const mspModes = [
    { id: 1, name: "Direct" },
    { id: 2, name: "MSP-DotStaff" },
    { id: 3, name: "MSP-ACRO" },
    { id: 4, name: "MSP-CAI" },
    { id: 5, name: "MSP-TAFFIN" },
    { id: 6, name: "Others" },
  ];

  const schema = {};
  const doSubmit = () => { };

  const {
    validate,
    handleChange,
    stateData,
    setStateData,
    stateErrors,
    setStateErrors,
    handleSubmit,
  } = useForm(schema, doSubmit);

  const { renderSelect, renderInput, renderTextArea } = useControls(
    stateData,
    stateErrors,
    handleChange,
    schema
  );

  useEffect(() => {
    setStateList(geo.getStateList());
  }, []);

  useEffect(() => {
    if (!selectedObj) return;

    const parsedData = {
      id: selectedObj.performerid,
      ipcsId: selectedObj.maxloans,
      recruiter: selectedObj.recruiter?.fuid || 0,
      subcontractor: selectedObj.validId,
      Rate: selectedObj.rates?.subcontractorRate,
      Recruiter_Rate: selectedObj.rates?.recruiterRate,
    };

    let appShortName = "";
    let appEntity = null;
    if (selectedObj.performerData) {
      try {
        const parsedApp = JSON.parse(selectedObj.performerData);
        if (parsedApp) {
          appShortName = Object.keys(parsedApp)[1] || "";
          appEntity = parsedApp[appShortName];

          if (appEntity) {
            Object.keys(appEntity).forEach((key) => {
              if (["@xmlns:xsd", "@xmlns:xsi", "EntityId"].includes(key)) return;
              if (appEntity[key]) {
                Object.entries(appEntity[key]).forEach(([k, v]) => {
                  if (!Array.isArray(v)) {
                    parsedData[k.replace("@", "")] = v;
                  }
                });
              }
            });
          }
        }
      } catch (e) {
        console.error("Error parsing performerData: ", e);
      }
    }

    setSubContractorList(filterSubcontractorList);
    setRecruiterList(filterRecruiterList);
    setEntityName(appShortName);
    setAWorkfinityAppEntity(appEntity);
    setStateData(parsedData);
    setStateErrors({});
    validate(parsedData);
  }, [selectedObj, filterSubcontractorList, filterRecruiterList]);

  // Calculate derived fields
  useEffect(() => {
    if (
      stateData.CandidateEntity === "W2 Basis" &&
      stateData.Rate_hour_Paid_to_Candidate &&
      stateData.Tax
    ) {
      const rate = parseFloat(stateData.Rate_hour_Paid_to_Candidate);
      const tax = parseFloat(stateData.Tax);
      const finalRate = rate + (rate * tax) / 100;
      setStateData((prev) => ({ ...prev, finalRate }));
    }
  }, [stateData.CandidateEntity, stateData.Rate_hour_Paid_to_Candidate, stateData.Tax]);

  useEffect(() => {
    if (stateData.StartDate && stateData.EngagementDuration) {
      const start = new Date(stateData.StartDate);
      start.setMonth(start.getMonth() + parseInt(stateData.EngagementDuration));
      const endDate = `${start.getDate()}-${getMonthName(start.getMonth() + 1)}-${start.getFullYear()}`;
      setStateData((prev) => ({ ...prev, EndDate: endDate }));
    }
  }, [stateData.StartDate, stateData.EngagementDuration]);

  // Handle Partner Address auto-fill
  const partnerData = useMemo(() => {
    const list = viewType === "subcontractor" ? subContractorList : recruiterList;
    const id = viewType === "subcontractor" ? stateData.subcontractor : stateData.recruiter;
    return list?.find((x) => x.fuid === parseInt(id));
  }, [viewType, stateData.subcontractor, stateData.recruiter, subContractorList, recruiterList]);

  useEffect(() => {
    if (partnerData) {
      setStateData((prev) => ({
        ...prev,
        AddressLine1: partnerData.accessKey,
        AddressLine2: partnerData.portalUrl,
        City1: partnerData.city,
        State1: partnerData.state,
        Zip1: partnerData.zip,
      }));
    }
  }, [partnerData]);

  const formatFieldLabel = (fieldName) =>
    fieldName
      .replace(/^@/, "")
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getEnrollmentFieldNames = (sectionName) => {
    const section = aWorkfinityAppEntity?.[sectionName];
    if (!section) return [];

    const priority = [
      "Company_Name",
      "Company_Email",
      "Company_Phone",
      "Company_Website",
      "Company_Address_Line1",
      "Company_Address_Line2",
      "Company_City",
      "Company_State",
      "Company_County",
      "Company_Country",
      "Company_Zip_Code",
      "Key_Contact1_First_Name",
      "Key_Contact1_Middle_Name",
      "Key_Contact1_Last_Name",
      "Key_Contact1_Title",
      "Key_Contact1_Email",
      "Key_Contact1_Mobile",
      "Key_Contact2_First_Name",
      "Key_Contact2_Middle_Name",
      "Key_Contact2_Last_Name",
      "Key_Contact2_Title",
      "Key_Contact2_Email",
      "Key_Contact2_Mobile",
      "Key_Contact3_First_Name",
      "Key_Contact3_Middle_Name",
      "Key_Contact3_Last_Name",
      "Key_Contact3_Title",
      "Key_Contact3_Email",
      "Key_Contact3_Mobile",
    ];

    const fieldNames = Object.entries(section)
      .filter(([fieldName, value]) => {
        const normalizedName = fieldName.replace(/^@/, "");
        return (
          normalizedName !== "EntityId" &&
          !fieldName.startsWith("@xmlns") &&
          !Array.isArray(value) &&
          (typeof value !== "object" || value === null)
        );
      })
      .map(([fieldName]) => fieldName.replace(/^@/, ""));

    const orderedFields = priority.filter((fieldName) => fieldNames.includes(fieldName));
    const remainingFields = fieldNames.filter((fieldName) => !priority.includes(fieldName));
    return [...orderedFields, ...remainingFields];
  };

  const getFieldIcon = (fieldName) => {
    const lower = fieldName.toLowerCase();
    if (lower.includes("name")) return "fa-user";
    if (lower.includes("email")) return "fa-envelope-o";
    if (lower.includes("phone") || lower.includes("mobile")) return "fa-phone";
    if (lower.includes("website") || lower.includes("url")) return "fa-globe";
    if (lower.includes("address")) return "fa-map-marker";
    if (lower.includes("city")) return "fa-building-o";
    if (lower.includes("state")) return "fa-globe";
    if (lower.includes("zip") || lower.includes("postal")) return "fa-hashtag";
    if (lower.includes("country")) return "fa-flag-o";
    if (lower.includes("title")) return "fa-vcard-o";
    return "fa-keyboard-o";
  };

  const renderEnrollmentFields = (sectionName) => {
    const fields = getEnrollmentFieldNames(sectionName);
    if (fields.length === 0) {
      return (
        <div className="col-md-12">
          <p className="form-control-label bg-admin-light border-admin-left p-2 w-100">
            No onboarding form details available.
          </p>
        </div>
      );
    }

    return fields.map((fieldName) => (
      <div className="col-md-4" key={`${sectionName}-${fieldName}`}>
        {renderInput(fieldName, formatFieldLabel(fieldName), "text", null, false, 0, undefined, getFieldIcon(fieldName))}
      </div>
    ));
  };

  // --- Render UI based on viewType ---
  const renderSection = () => {
    switch (viewType) {
      case "agency":
        return (
          <div className="row">
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-building" /> Agency & Project Engagement
              </h5>
            </div>
            <div className="col-md-6">{renderInput("AgencyName", "Agency Name", "text", null, false, 0, undefined, "fa-building-o")}</div>
            <div className="col-md-6">{renderSelect("State", "State", stateList, "name", "name", "fa-map-marker")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-briefcase" /> Position Details
              </h5>
            </div>
            <div className="col-md-6">{renderInput("PositionTitle", "Position Title", "text", null, false, 0, undefined, "fa-id-card-o")}</div>
            <div className="col-md-6">{renderInput("PositionID", "Position ID", "text", null, false, 0, undefined, "fa-hashtag")}</div>
            <div className="col-md-12">{renderInput("PositionLocation", "Position Location", "text", null, false, 0, undefined, "fa-map-pin")}</div>
            <div className="col-md-12">{renderTextArea("ProjectDescription", "Project Description", "fa-file-text-o")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-calendar-check-o" /> Engagement Terms & Rates
              </h5>
            </div>
            <div className="col-md-3">{renderInput("StartDate", "Start Date", "date", null, false, 0, undefined, "fa-calendar")}</div>
            <div className="col-md-3">{renderInput("EngagementDuration", "Duration (Months)", "text", null, false, 0, undefined, "fa-clock-o")}</div>
            <div className="col-md-1 d-flex align-items-center justify-content-center"><label htmlFor="" className="form-control-label">OR</label></div>
            <div className="col-md-2">{renderInput("Engagement_Day", "Days", "number", null, false, 0, undefined, "fa-sun-o")}</div>
            <div className="col-md-3">{renderInput("EndDate", "End Date", "text", null, false, 0, undefined, "fa-calendar-times-o")}</div>
            <div className="col-md-6">{renderInput("RatePayableIpcs", "Rate/Hour (IPCS) $", "text", null, false, 0, undefined, "fa-money")}</div>
            <div className="col-md-6">{renderInput("Fixed_Hours", "Fixed Hours", "text", null, false, 0, undefined, "fa-hourglass-half")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> Agency Primary Contact
              </h5>
            </div>
            <div className="col-md-6">{renderInput("AgencyContact", "Name", "text", null, false, 0, undefined, "fa-user")}</div>
            <div className="col-md-6">{renderInput("AgencyTitle", "Title", "text", null, false, 0, undefined, "fa-vcard-o")}</div>
            <div className="col-md-6">{renderInput("AgencyEmail", "Email", "text", null, false, 0, undefined, "fa-envelope-o")}</div>
            <div className="col-md-6">{renderInput("AgencyPhone", "Phone", "phone", null, false, 0, undefined, "fa-phone")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> Agency Secondary Contact
              </h5>
            </div>
            <div className="col-md-6">{renderInput("AgencyContactName2", "Name", "text", null, false, 0, undefined, "fa-user")}</div>
            <div className="col-md-6">{renderInput("AgencyContactTitle2", "Title", "text", null, false, 0, undefined, "fa-vcard-o")}</div>
            <div className="col-md-6">{renderInput("AgencyContactEmail2", "Email", "text", null, false, 0, undefined, "fa-envelope-o")}</div>
            <div className="col-md-6">{renderInput("AgencyContactPhone2", "Phone No.", "phone", null, false, 0, undefined, "fa-phone")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> Agency Additional Contact
              </h5>
            </div>
            <div className="col-md-6">{renderInput("AgencyContactName3", "Name", "text", null, false, 0, undefined, "fa-user")}</div>
            <div className="col-md-6">{renderInput("AgencyContactTitle3", "Title", "text", null, false, 0, undefined, "fa-vcard-o")}</div>
            <div className="col-md-6">{renderInput("AgencyContactEmail3", "Email", "text", null, false, 0, undefined, "fa-envelope-o")}</div>
            <div className="col-md-6">{renderInput("AgencyContactPhone3", "Phone No.", "phone", null, false, 0, undefined, "fa-phone")}</div>
          </div>
        );

      case "msp":
        return (
          <div className="row">
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-shield" /> MSP & Administrative Configuration
              </h5>
            </div>
            <div className="col-md-6">{renderSelect("Msp_Mode", "MSP Mode", mspModes, "name", "name", "fa-cog")}</div>
            <div className="col-md-6">{renderInput("Msp_AdminFee", "MSP Admin Fee %", "text", null, false, 0, undefined, "fa-percent")}</div>
            <div className="col-md-6">{renderInput("Hub_CompanyFee", "HUB Fee %", "text", null, false, 0, undefined, "fa-percent")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> MSP Primary Contact
              </h5>
            </div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Name", "Name", "text", null, false, 0, undefined, "fa-user")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Email", "Email", "text", null, false, 0, undefined, "fa-envelope-o")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact1_Phone", "Phone", "phone", null, false, 0, undefined, "fa-phone")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> MSP Secondary Contact
              </h5>
            </div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Name", "Name", "text", null, false, 0, undefined, "fa-user")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Email", "Email", "text", null, false, 0, undefined, "fa-envelope-o")}</div>
            <div className="col-md-4">{renderInput("Msp_Contact2_Phone", "Phone", "phone", null, false, 0, undefined, "fa-phone")}</div>

            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-sticky-note-o" /> Engagement Notes
              </h5>
            </div>
            <div className="col-md-12">{renderTextArea("mspNotes", "Notes", "fa-file-text-o")}</div>
          </div>
        );

      case "candidate":
        return (
          <div className="row">
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-circle-o" /> Candidate Basic Details
              </h5>
            </div>
            <div className="col-md-12">{renderInput("ipcsId", "IPCS Existing ID")}</div>
            <div className="col-lg-4">{renderInput("First_Name", "First Name")}</div>
            <div className="col-lg-4">{renderInput("Middle_Name", "Middle Name")}</div>
            <div className="col-lg-4">{renderInput("Last_Name", "Last Name")}</div>
            <div className="col-lg-6">{renderInput("Email", "Email")}</div>
            <div className="col-lg-6">{renderInput("Mobile_No", "Mobile Number", "phone")}</div>

            {stateData.CandidateEntity === "1099 Basis" && (
              <div className="col-md-12">
                {renderInput("Rate_hour_Paid_to_Candidate", "Rate/hour-Paid to Candidate $")}
              </div>
            )}

            {stateData.CandidateEntity === "W2 Basis" && (
              <>
                <div className="col-md-6">{renderInput("Rate_hour_Paid_to_Candidate", "Rate/hour-Paid to Candidate $")}</div>
                <div className="col-md-6">{renderInput("Tax", "Tax Rate %")}</div>
                <div className="col-md-6">{renderInput("finalRate", "Final Rate/hour-Paid to Candidate $")}</div>
                {stateData.w9_Type === "W2 Hourly with Benefits" && (
                  <div className="col-md-6">{renderInput("costOfBenefit", "Cost Of Benefit $")}</div>
                )}
              </>
            )}
            <div className="col-md-12">{renderTextArea("notes", "Notes")}</div>
          </div>
        );

      case "subcontractor":
        return (stateData.CandidateEntity === "C2C Basis" || !stateData.CandidateEntity) ? (
          <div className="row">
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-handshake-o" /> Subcontractor Details
              </h5>
            </div>
            <div className="col-md-6">{renderSelect("subcontractor", "Select Subcontractor", subContractorList, "label", "fuid")}</div>
            <div className="col-md-6">{renderInput("Rate", "Rate/Hour Paid to Subcontractor $")}</div>
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-list-alt" /> Subcontractor Enrollment Details
              </h5>
            </div>
            {renderEnrollmentFields("subcontractorenrollmentform")}
          </div>
        ) : null;

      case "recruiter":
        return (
          <div className="row">
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-user-secret" /> Recruiter Details
              </h5>
            </div>
            <div className="col-md-8">{renderSelect("recruiter", "Recruiter", recruiterList, "label", "fuid")}</div>
            <div className="col-md-4">{renderInput("Recruiter_Rate", "Rate/Hour Paid to Recruiter $")}</div>
            <div className="col-md-12">
              <h5 className="as-section-title">
                <i className="fa fa-list-alt" /> Recruiter Enrollment Details
              </h5>
            </div>
            {renderEnrollmentFields("recruiterenrollmentform")}
          </div>
        );

      default:
        return null;
    }
  };

  const getHeaderIcon = () => {
    switch (viewType) {
      case "candidate": return "fa-user-circle-o";
      case "subcontractor": return "fa-handshake-o";
      case "recruiter": return "fa-user-secret";
      case "agency": return "fa-building";
      case "msp": return "fa-shield";
      default: return "fa-list-alt";
    }
  }

  return (
    <div className={embedded ? "col-md-12 p-0" : "as-view-candidate-container col-md-12 p-0"}>
      <style>{`
        .as-form-header {
          display: flex !important;
          align-items: flex-start !important;
          justify-content: space-between !important;
          flex-wrap: wrap !important;
          gap: 18px !important;
          padding: 20px 24px !important;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
          border-bottom: 1px solid rgba(226, 232, 240, 0.9) !important;
          position: relative !important;
          overflow: hidden !important;
          border-radius: 12px 5px 0 0 !important;
          margin: ${embedded ? "0 0 25px 0" : "-24px -24px 25px -24px"} !important;
          width: ${embedded ? "100%" : "calc(100% + 48px)"} !important;
        }
        .as-form-header-left {
          display: flex !important;
          align-items: flex-start !important;
          gap: 14px !important;
          min-width: 260px !important;
          flex: 1 1 360px !important;
        }
        .as-form-header-right {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          flex-wrap: wrap !important;
          justify-content: flex-end !important;
          margin-left: auto !important;
          position: relative !important;
          z-index: 1 !important;
          flex-shrink: 0 !important;
        }
        .as-header-back-btn {
          background: #ffffff !important;
          border: 1.5px solid #cbd5e1 !important;
          border-radius: 8px !important;
          padding: 6px 14px !important;
          color: #475569 !important;
          font-size: 12.5px !important;
          font-weight: 600 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
          outline: none !important;
          height: 38px !important;
        }
        .as-header-back-btn:hover {
          background: #f1f5f9 !important;
          border-color: #94a3b8 !important;
          color: #0f172a !important;
          transform: translateY(-1px) !important;
        }
        .as-form-header-icon {
          width: 44px !important;
          height: 44px !important;
          border-radius: 12px !important;
          background: linear-gradient(135deg, #2563eb 0%, #0284c7 100%) !important;
          border: 1px solid rgba(37, 99, 235, 0.18) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #ffffff !important;
          font-size: 18px !important;
          flex-shrink: 0 !important;
          box-shadow: 0 12px 22px rgba(15, 23, 42, 0.16) !important;
        }
        .as-form-header-text {
          position: relative !important;
          z-index: 1 !important;
        }
        .as-form-header-title {
          color: #0f172a !important;
          font-size: 16px !important;
          font-weight: 800 !important;
          margin: 0 !important;
          line-height: 1.2 !important;
        }
        .as-form-header-sub {
          color: #64748b !important;
          font-size: 13px !important;
          margin-top: 6px !important;
          font-weight: 500 !important;
          max-width: 620px !important;
        }
        .as-form-header-meta {
          display: flex !important;
          align-items: center !important;
          gap: 7px !important;
          flex-wrap: wrap !important;
          margin-top: 10px !important;
        }
        .as-meta-pill {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 4px 12px !important;
          border-radius: 99px !important;
          background: #ffffff !important;
          border: 1px solid #cbd5e1 !important;
          color: #475569 !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          height: auto !important;
          line-height: 1.5 !important;
        }
        .as-meta-pill i { font-size: 11px !important; }
        .as-meta-pill.meta-id i { color: #1d4ed8 !important; }
        .as-meta-pill.meta-person i { color: #047857 !important; }
        .as-meta-pill.meta-email i { color: #0369a1 !important; }
        .as-meta-pill.meta-entity i { color: #6d28d9 !important; }
        .as-meta-pill.meta-benefits i { color: #b45309 !important; }
        .as-meta-pill.meta-ciid i { color: #dc2626 !important; }

        .as-section-title {
          font-size: 13px !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          padding: 8px 12px !important;
          background: #f1f5f9 !important;
          border-left: 4px solid #2563eb !important;
          border-radius: 0 6px 6px 0 !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          width: 100% !important;
          margin-top: 20px !important;
          margin-bottom: 15px !important;
        }

        /* Input matching AddWorkflowPerformer */
        .form-control-label {
          font-size: 11px !important; font-weight: 700 !important; color: #64748b !important;
          text-transform: uppercase !important; letter-spacing: 0.7px !important;
          margin-bottom: 6px !important; display: flex !important;
          align-items: center !important; gap: 5px !important;
        }
        .form-group { margin-bottom: 16px; }
        .wf-input-wrap { position: relative; }
        .wf-input-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          color: #94a3b8; font-size: 13px; pointer-events: none;
          transition: color 0.2s ease; z-index: 1;
        }
        .wf-input-wrap:focus-within .wf-input-icon { color: #2563eb; }

        .form-control {
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          padding: 8px 13px !important;
          font-size: 13px !important;
          color: #1e293b !important;
          background: #ffffff !important;
          transition: all 0.2s cubic-bezier(0.4,0,0.2,1) !important;
          box-shadow: 0 1px 3px 0 rgba(0,0,0,0.03) !important;
          width: 100% !important;
          height: auto !important;
        }
        .form-control.wf-input-withicon {
          padding-left: 35px !important;
        }
        .form-control:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.1), 0 2px 6px -1px rgba(37,99,235,0.08) !important;
          outline: none !important; background: #fafbff !important;
        }
        select.form-control {
          appearance: none !important; -webkit-appearance: none !important;
          background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E") no-repeat right 12px center !important;
          padding-right: 32px !important;
        }
      `}</style>

      {!hideHeader && (
        <div className="as-form-header">
          <div className="as-form-header-left">
            <div className="as-form-header-icon">
              <i className={`fa ${getHeaderIcon()}`} />
            </div>
            <div className="as-form-header-text">
              <p className="as-form-header-title">
                {title || `${viewType?.charAt(0).toUpperCase() + viewType?.slice(1)} Details`}
              </p>
              <p className="as-form-header-sub">
                Viewing captured onboarding form data and profile configurations for this candidate application.
              </p>
              <div className="as-form-header-meta">
                <span className="as-meta-pill meta-id">
                  <i className="fa fa-id-badge" /> ID: {selectedObj?.performerid || "N/A"}
                </span>
                {(selectedObj?.performername || stateData.First_Name) && (
                  <span className="as-meta-pill meta-person">
                    <i className="fa fa-user" /> {selectedObj?.performername || `${stateData.First_Name} ${stateData.Last_Name}`}
                  </span>
                )}
                {(selectedObj?.performeremail || stateData.Email) && (
                  <span className="as-meta-pill meta-email">
                    <i className="fa fa-envelope-o" /> {selectedObj?.performeremail || stateData.Email}
                  </span>
                )}
                {stateData.CandidateEntity && (
                  <span className="as-meta-pill meta-entity">
                    <i className="fa fa-building" /> {stateData.CandidateEntity}
                  </span>
                )}
                {viewType === "agency" && stateData.AgencyName && (
                  <span className="as-meta-pill meta-entity">
                    <i className="fa fa-building" /> {stateData.AgencyName}
                  </span>
                )}
                {viewType === "msp" && stateData.Msp_Mode && (
                  <span className="as-meta-pill meta-entity">
                    <i className="fa fa-shield" /> {stateData.Msp_Mode}
                  </span>
                )}
                {stateData.w9_Type && (
                  <span className="as-meta-pill meta-benefits">
                    <i className="fa fa-gift" /> {stateData.w9_Type}
                  </span>
                )}
                {wfTask?.ciid > 0 && (
                  <span className="as-meta-pill meta-ciid">
                    <i className="fa fa-hashtag" /> CIID {wfTask.ciid}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="as-form-header-right">
            <button
              type="button"
              className="as-header-back-btn"
              onClick={() => {
                if (onClose) {
                  onClose();
                } else if (handleDurationShowHide) {
                  handleDurationShowHide("", "", -1);
                }
              }}
            >
              <i className="fa fa-times" aria-hidden="true" />
              Close
            </button>
          </div>
        </div>
      )}

      <div className="col-md-12">
        <fieldset disabled={true}>
          {renderSection()}

          {(viewType === "subcontractor" || viewType === "recruiter") &&
            partnerData &&
            getEnrollmentFieldNames(
              viewType === "subcontractor"
                ? "subcontractorenrollmentform"
                : "recruiterenrollmentform",
            ).length === 0 && (
              <div className="row">
                <div className="col-md-6">{renderInput("AddressLine1", "Address Line 1")}</div>
                <div className="col-md-6">{renderInput("AddressLine2", "Address Line 2")}</div>
                <div className="col-md-4">{renderInput("City1", "City")}</div>
                <div className="col-md-4">{renderSelect("State1", "State", stateList, "name", "name")}</div>
                <div className="col-md-4">{renderInput("Zip1", "Zip")}</div>
              </div>
            )}
        </fieldset>
      </div>
    </div>
  );
};

export default ViewCandidate;
